import type { IMergeFieldAttrs } from '@levitate/rich-text';
import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownSelectTrigger,
	DropdownSubMenu,
	DropdownSubTrigger,
} from '../../../../../components-v2/Dropdown';
import { MEETING_MERGE_FIELD_GROUPS } from './meetingMergeFields';

const styleSheet = StyleSheet.create({
	// Sits inside the editor toolbar, so drop the default select's border/shadow (mirrors the
	// texting MergeFieldsDropdown trigger).
	trigger: {
		backgroundColor: 'transparent',
		border: 'none',
		boxShadow: 'none',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		height: 'auto',
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
	},
	content: {
		minWidth: 200,
	},
	groupContent: {
		maxHeight: 360,
		minWidth: 200,
		overflowY: 'auto',
	},
});

interface IProps {
	onInsert: (field: IMergeFieldAttrs) => void;
}

/**
 * Design-system merge-field picker for the meeting-note editor: a `Dropdown` whose top level lists
 * the field groups, each opening a flyout submenu of tokens that insert via the editor's
 * `insertMergeField`. Mirrors the texting MergeFieldsDropdown.
 */
export function MeetingMergeFieldMenu({ onInsert }: IProps) {
	return (
		<Dropdown modal={false}>
			<DropdownSelectTrigger styles={[styleSheet.trigger]}>Merge Fields</DropdownSelectTrigger>
			<DropdownContent align='start' sideOffset={4} styles={[styleSheet.content]}>
				{MEETING_MERGE_FIELD_GROUPS.map(group => (
					<DropdownSubMenu key={group.label}>
						<DropdownSubTrigger>{group.label}</DropdownSubTrigger>
						<DropdownContent styles={[styleSheet.groupContent]}>
							{group.fields.map(field => (
								<DropdownItem
									key={field.symbol}
									onClick={() =>
										onInsert(
											// value-mode (contact) fields serialize as data-placeholder spans; raw-mode as {{...}}.
											field.value
												? { text: field.text, value: field.value }
												: { text: field.text, symbol: field.symbol }
										)
									}
								>
									{field.menuLabel ?? field.text}
								</DropdownItem>
							))}
						</DropdownContent>
					</DropdownSubMenu>
				))}
			</DropdownContent>
		</Dropdown>
	);
}
